import type { BusinessOverviewResponse } from '@pulsify/shared/dto'
import { useState } from 'react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { METRIC_COLORS } from '~/shared/styles/chart-palette'
import { Card } from '~/shared/ui/card'
import { StackedBars, type StackedBarsSegment } from '~/shared/ui/stacked-bars/stacked-bars'
import { aggregateWeeks, type StackedBarsPoint } from '~/shared/ui/stacked-bars/stacked-bars-data'

type ReachKey = 'impressions' | 'views'
type ReachPeriod = 'day' | 'week'

const REACH_KEYS: readonly ReachKey[] = ['views', 'impressions']

function reachSegments(): StackedBarsSegment<ReachKey>[] {
  return [
    { key: 'views', label: m.views(), color: METRIC_COLORS.views },
    { key: 'impressions', label: m.impressions(), color: METRIC_COLORS.impressions },
  ]
}

interface ReachChartProps {
  daily: BusinessOverviewResponse['daily']
  total: number
  change: number | null
  locale: string
  className?: string
}

export function ReachChart({ daily, total, change, locale, className }: ReachChartProps) {
  const [period, setPeriod] = useState<ReachPeriod>('day')
  const days: StackedBarsPoint<ReachKey>[] = daily.map((entry) => ({
    date: entry.day,
    values: { impressions: entry.impressions, views: entry.views },
  }))
  const series = period === 'week' ? aggregateWeeks(days, REACH_KEYS) : days

  return (
    <Card className={cn('flex min-w-0 flex-col p-6 sm:p-7', className)}>
      <StackedBars
        series={series}
        segments={reachSegments()}
        bucket={period}
        title={m.overviewReach()}
        subtitle={m.overviewChartSubtitle()}
        headline={{
          value: total,
          label: m.impressions(),
          change,
          changeLabel: m.overviewDeltaVsPrevious(),
        }}
        periods={[
          { value: 'day', label: m.chartPeriodDay() },
          { value: 'week', label: m.chartPeriodWeek() },
        ]}
        period={period}
        onPeriodChange={setPeriod}
        height={220}
        legend
        locale={locale}
      />
    </Card>
  )
}
